import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';

const REMINDER_TYPE_LABELS = {
  1: '5-Day (All Players)',
  2: '2-Day (No Response)',
  3: 'Spare Availability',
  4: '4-Day (No Response)',
  5: '3-Day (No Response)',
  6: 'Missing Score',
};

const STATUS_LABELS = {
  1: 'Queued',
  2: 'Sent',
  3: 'Failed',
};

const STATUS_COLORS = {
  1: 'warning',
  2: 'success',
  3: 'error',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const ReminderEmailDashboardPage = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summaries, setSummaries] = useState([]);

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailError, setDetailError] = useState(null);

  // Filter
  const [typeFilter, setTypeFilter] = useState('');

  const fetchSummaries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await unifiedApi.getReminderBatchSummaries(leagueId);
      setSummaries(data || []);
    } catch (err) {
      setError('Failed to load reminder email data.');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    fetchSummaries();
  }, [fetchSummaries]);

  const handleViewDetails = async (gameId, reminderType) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError(null);
    try {
      const data = await unifiedApi.getReminderGameDetails(leagueId, gameId, reminderType);
      setDetailData(data);
    } catch (err) {
      setDetailError('Failed to load reminder details.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setDetailData(null);
    setDetailError(null);
  };

  const filteredSummaries = typeFilter
    ? summaries.filter(s => s.reminderType === parseInt(typeFilter))
    : summaries;

  if (loading) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/admin/leagues/${leagueId}`)}
          sx={{ mb: 2 }}
        >
          Back to League Admin
        </Button>
        <Typography variant="h4" gutterBottom>
          Reminder Emails
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Reminder Type</InputLabel>
          <Select
            value={typeFilter}
            label="Reminder Type"
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <MenuItem value="">All Types</MenuItem>
            <MenuItem value="1">5-Day (All Players)</MenuItem>
            <MenuItem value="2">2-Day (No Response)</MenuItem>
            <MenuItem value="3">Spare Availability</MenuItem>
            <MenuItem value="4">4-Day (No Response)</MenuItem>
            <MenuItem value="5">3-Day (No Response)</MenuItem>
            <MenuItem value="6">Missing Score</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchSummaries}
        >
          Refresh
        </Button>
      </Stack>

      {filteredSummaries.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <EmailIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No reminder emails sent yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Reminder emails will appear here once they are sent for upcoming games.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Game</TableCell>
                <TableCell>Game Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Sent Date</TableCell>
                <TableCell align="center">Sent</TableCell>
                <TableCell align="center">Failed</TableCell>
                <TableCell align="center">Queued</TableCell>
                <TableCell align="center">Total</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSummaries.map((summary, idx) => (
                <TableRow key={`${summary.gameId}-${summary.reminderType}-${idx}`}>
                  <TableCell>{summary.gameDescription}</TableCell>
                  <TableCell>{formatDate(summary.gameDate)}</TableCell>
                  <TableCell>
                    <Chip
                      label={REMINDER_TYPE_LABELS[summary.reminderType] || 'Unknown'}
                      size="small"
                      color="primary"
                      variant="outlined"
                      clickable={false}
                      onClick={() => {}}
                    />
                  </TableCell>
                  <TableCell>{formatDate(summary.batchDate)}</TableCell>
                  <TableCell align="center">
                    <Chip label={summary.sentCount} size="small" color="success" clickable={false} onClick={() => {}} />
                  </TableCell>
                  <TableCell align="center">
                    {summary.failedCount > 0 ? (
                      <Chip label={summary.failedCount} size="small" color="error" clickable={false} onClick={() => {}} />
                    ) : (
                      '0'
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {summary.queuedCount > 0 ? (
                      <Chip label={summary.queuedCount} size="small" color="warning" clickable={false} onClick={() => {}} />
                    ) : (
                      '0'
                    )}
                  </TableCell>
                  <TableCell align="center">{summary.totalCount}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => handleViewDetails(summary.gameId, summary.reminderType)}
                    >
                      Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onClose={handleCloseDetail} maxWidth="md" fullWidth>
        <DialogTitle>
          {detailData ? `${detailData.gameDescription} — ${formatDate(detailData.gameDate)}` : 'Reminder Details'}
        </DialogTitle>
        <DialogContent>
          {detailLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {detailError && (
            <Alert severity="error" sx={{ mb: 2 }}>{detailError}</Alert>
          )}
          {detailData && !detailLoading && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Recipient</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Queued</TableCell>
                    <TableCell>Sent</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailData.logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>{log.recipientName}</TableCell>
                      <TableCell>{log.recipientEmail}</TableCell>
                      <TableCell>
                        <Chip
                          label={REMINDER_TYPE_LABELS[log.reminderType] || 'Unknown'}
                          size="small"
                          variant="outlined"
                          clickable={false}
                          onClick={() => {}}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={STATUS_LABELS[log.status] || 'Unknown'}
                          size="small"
                          color={STATUS_COLORS[log.status] || 'default'}
                          clickable={false}
                          onClick={() => {}}
                        />
                      </TableCell>
                      <TableCell>{formatDate(log.queuedAt)}</TableCell>
                      <TableCell>{log.sentAt ? formatDate(log.sentAt) : '—'}</TableCell>
                      <TableCell sx={{ color: 'error.main', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {log.errorMessage || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetail}>Close</Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
};

export default ReminderEmailDashboardPage;
